import { StandbyInput, StandbyInputMode } from "@herbjoy/sheet-hotkeys";
import { evalInChildScope } from "../../core/data-utils";

/**
 * Factory function returning the directive definition for
 * defining an element as a standby input placeholder for
 * another element. The element will be activated when it is
 * given focus.
 * @param {any} mode The bindable mode of the input. Can be Active(0) or Inactive(1). Defaults to Inactive.
 *   See StandbyInputMode for strongly typed values.
 * @param {any} disabled If true, the input will never activate upon focus. Explicit mode switching will still work.
 * @param {any} onActivating Method binding passed the $event data just before activating the input.
 * @param {any} onActivated Method binding passed the $event data just after activating the input.
 * @param {any} onDeactivating Method binding passed the $event data just before deactivating the input.
 * @param {any} onDeactivated Method binding passed the $event data just after deactivating the input.
 * @param {any} onFocusing Method binding passed the $event data just before focusing on the input.
 * @example
 * <apex-standby-input mode="$ctrl.mode" on-activating="$ctrl.onActivating($event)">
 *     <apex-standby-input-template>
 *         {{ $ctrl.number }}
 *     </apex-standby-input-template>
 *     <apex-active-input-template>
 *         <input type="number" class="form-control" ng-model="$ctrl.number" />
 *     </apex-active-input-template>
 * </apex-standby-input>
 */
export function StandbyInputDirective($compile) {
    'ngInject';
    return {
        restrict: "EA",
        scope: false,
        terminal: true,
        link: function (scope, el, attr) {
            const standbyHtml = el.find("apex-standby-input-template");
            const activeHtml = el.find("apex-active-input-template");

            // Remove all existing children from the root element.
            el.html("");

            const options = {
                mode: getMode(scope, attr),
                disabled: getDisabled(scope, attr),
                onActivating: getOnActivating(scope, attr),
                onActivated: getOnActivated(scope, attr),
                onDeactivating: getOnDeactivating(scope, attr),
                onDeactivated: getOnDeactivated(scope, attr),
                onFocusing: getOnFocusing(scope, attr)
            }

            const standbyInputOptions = {
                activeHtml: activeHtml,
                standbyHtml: standbyHtml,
                mode: options.mode,
                onActivating: (el, args) => scope.$apply(() => {
                    if (getDisabled(scope, attr)) {
                        args.cancel = true;
                        return;
                    }
                    el.classList.add("apex-standby-activating");
                    options.onActivating({ $event: args });
                }),
                onActivated: el => scope.$apply(() => {
                    addActiveStyles(el);
                    options.onActivated();
                }),
                onDeactivating: (el, args) => scope.$apply(() => {
                    el.classList.add("apex-standby-deactivating");
                    options.onDeactivating({ $event: args });
                }),
                onDeactivated: el => scope.$apply(() => {
                    addInactiveStyles(el);
                    options.onDeactivated();
                }),
                onFocusing: (el, args) => scope.$apply(() => {
                    // Consider the "apex-standby-focus" attribute as a marker for choosing which
                    // element should receive focus when focusing on an active element.
                    const focusTarget = el.querySelector("[apex-standby-focus]");
                    if (focusTarget) {
                        focusTarget.focus();
                        args.cancel = true;
                    }
                    options.onFocusing({ $event: args })
                })
            };

            const compilerOptions = {
                compile: html => {
                    // The standby input library expects the compiler to return the compiled element.
                    return $compile(html)(scope)[0];
                },
                attach: element => {
                    el[0].appendChild(element);
                },
                detach: element => {
                    el[0].removeChild(element);
                }
            };

            const input = new StandbyInput(standbyInputOptions, compilerOptions);
            input.attach();
            // Invoke activation logic immediately upon attaching.
            options.mode === StandbyInputMode.Active ? addActiveStyles(input.getElement())
                : addInactiveStyles(input.getElement());

            scope.$watch(attr.mode, () => {
                // Changes to the mode should be treated as a bound change, so
                // explicitly alter the component value.
                input.changeMode(getMode(scope, attr));
            });

            scope.$on("$destroy", () => {
                if (input) {
                    input.detach();
                }
            });

            function addActiveStyles(el) {
                el.classList.remove("apex-standby-deactivating");
                el.classList.add("apex-standby-activated");
            }

            function addInactiveStyles(el) {
                el.classList.remove("apex-standby-activating");
                if (!options.disabled) {
                    el.classList.add("apex-standby-activatable");
                }
            }
        }
    }
}

function getMode(scope, attr) {
    const value = scope.$eval(attr.mode);
    if (StandbyInputMode[value] !== undefined) {
        // Only use the value if it's defined in the modes enum.
        if (value === StandbyInputMode.Active || value === StandbyInputMode.Inactive) {
            return value;
        }
        // Reverse look up enum value based on enum key.
        return StandbyInputMode[value];
    }
    return StandbyInputMode.Inactive;
}

function getDisabled(scope, attr) {
    return scope.$eval(attr.disabled);
}

function getOnActivating(scope, attr) {
    return args => evalInChildScope(scope, args, attr.onActivating || "");
}

function getOnActivated(scope, attr) {
    return args => evalInChildScope(scope, args, attr.onActivated || "");
}

function getOnDeactivating(scope, attr) {
    return args => evalInChildScope(scope, args, attr.onDeactivating || "");
}

function getOnDeactivated(scope, attr) {
    return args => evalInChildScope(scope, args, attr.onDeactivated || "");
}

function getOnFocusing(scope, attr) {
    return args => evalInChildScope(scope, args, attr.onFocusing || "");
}
